import React from "react";
import { connect } from "react-redux";
import * as PropTypes from "prop-types";
import { withRouter } from 'react-router-dom'
import styles from "./HeroIntroPromo.module.css";
import elementConstant from "../../../../../shared/constants/element";
import * as elementsActions from "../../../../../redux/actions/elements";
import * as brazeActions from '../../../../../redux/actions/braze';
import {ContentCardBase} from "../ContentCardBase";
import MOBILE_SIZE from "../../../../../shared/constants/size";
import * as contentCardConstants
  from "../../../../../shared/constants/contentCards";

class HeroIntroPromo extends ContentCardBase {
  constructor(props) {
    super(props);
    // Support both 'extras' and 'e' (Braze SDK may use either)
    const cardExtras = props.cardData.extras || props.cardData.e || {};
    const { desktopVideoUrl, desktopImageUrl } = cardExtras;

    this.state = {
      videoUrl: desktopVideoUrl,
      imageUrl: desktopImageUrl,
      fadeOutClass: null,
      videoError: false
    };

    this.videoElRef = React.createRef();
  }

  componentDidMount() {
    this.updateVideo();
    const { contentCardRefreshDelay } = this.props;

    this.timeOut = setTimeout(() => {
      this.setState({ fadeOutClass: styles.fadeOut });
    }, parseInt(contentCardRefreshDelay, 10) - elementConstant.introBannerReduceTimeoutOffset);
  }

  componentWillUnmount() {
    clearTimeout(this.timeOut);
  }

  handleLoadedMetadata = () => {
    const { setCarouselSpeed } = this.props;
    const video = this.videoElRef.current;
    if (!video) return;
    const carouselSpeed = parseInt(video.duration * 1000 + 800, 10); // Add 0.8 seconds delay after video ends
    setCarouselSpeed(carouselSpeed);
  };

  redirectTo = (cardData) => {
    const { location, setHeroContentCardData, openDeliveryWidget } = this.props;

    // Support both 'url' and 'u' (Braze SDK may use either)
    const url = cardData.url || cardData.u;

    // Guard against undefined url - only navigate if url exists and is valid
    if (!url) {
      return;
    }

    if(location?.pathname !== '/' || url.startsWith(contentCardConstants.EXTERNAL_URL_PREFIX)) {
      this.handleClick(cardData)
    } else {
      this.recordClick(cardData);
      setHeroContentCardData({contentCardHandleClick: this.handleDeferredClick, contentCardData: cardData});
      if(openDeliveryWidget) {
        openDeliveryWidget()
      }
    }
  }

  handleDeepLink = (anchor) => {
      const { setProductCategory } = this.props;
      setProductCategory(anchor);
      const element = document.getElementById(anchor);
      let scrollDelta = this.categoryElement.current ? this.categoryElement.current.getBoundingClientRect().height : elementConstant.scrollDeltaDefaultHeight;
      scrollDelta = window.innerWidth > MOBILE_SIZE ? scrollDelta : elementConstant.scrollDeltaDefault;
      const yPos = element ? (element.offsetTop - scrollDelta) : null;
      if (yPos) window.scrollTo({ top: yPos, left: 0, behavior: 'smooth'}); // eslint-disable-line
  }

  updateVideo() {
    const { cardData } = this.props;
    // Support both 'extras' and 'e' (Braze SDK may use either)
    const extras = cardData.extras || cardData.e || {};
    const desktopVideoUrl = extras?.desktopVideoUrl;
    const mobileVideoUrl = extras?.mobileVideoUrl;
    const desktopImageUrl = extras?.desktopImageUrl;
    const mobileImageUrl = extras?.mobileImageUrl;

    const videoFile =
      window.innerWidth <= elementConstant.introHeroBannerWidthThreshold
        ? mobileVideoUrl
        : desktopVideoUrl;

    const imageFile =
      window.innerWidth <= elementConstant.introHeroBannerWidthThreshold
        ? mobileImageUrl
        : desktopImageUrl;

    this.setState({ videoUrl: videoFile, imageUrl: imageFile });
  }

  handleVideoError = () => {
    this.setState({ videoError: true });
  };

  render() {
    const { fadeOutClass, videoUrl, imageUrl, videoError } = this.state;
    const { cardData } = this.props;
    // Support both 'url' and 'u' (Braze SDK may use either)
    const url = cardData.url || cardData.u;

    const { title, description } = cardData;
    // Support both 'extras' and 'e' (Braze SDK may use either)
    const extras = cardData.extras || cardData.e || {};
    const { mime, preheaderText, actionButtonText, actionButtonTextColor, actionButtonFill, titleColor, titleBold, titleItalic, desktopVideoUrl, mobileVideoUrl, desktopImageUrl, mobileImageUrl } = extras;

    const hasVideo = desktopVideoUrl || mobileVideoUrl;
    const hasImage = desktopImageUrl || mobileImageUrl;
    const showVideo = hasVideo && !videoError;
    const showImage = hasImage && !showVideo;

    const actionButton = actionButtonText !== undefined ? (
      <button
        type="button"
        className={styles.actionButton}
        style={{ color: actionButtonTextColor, backgroundColor: actionButtonFill }}
        onClick={() => url && this.redirectTo(cardData)}
      >
        {actionButtonText}
      </button>
    ) : null;

    const hasTextOverlay = preheaderText || title || description || actionButton;

    // Determine clickability: only clickable if URL exists AND no CTA button
    const hasCTA = actionButtonText !== undefined;
    const isCardClickable = url && !hasCTA;

    // Determine cursor style: pointer if clickable, default otherwise
    const cursorClass = isCardClickable ? 'cursor-pointer' : 'cursor-default';

    let mediaElement;
    if (showVideo) {
      mediaElement = (
        /* eslint-disable-next-line jsx-a11y/media-has-caption */
        <video
          autoPlay
          muted
          playsInline
          webkit-playsinline="webkit-playsinline"
          ref={this.videoElRef}
          onLoadedMetadata={this.handleLoadedMetadata}
          onError={this.handleVideoError}
          className={isCardClickable ? "cursor-pointer" : undefined}
          onClick={(e) => {
            if (isCardClickable) {
              e.stopPropagation();
              this.redirectTo(cardData);
            }
          }}
          onKeyDown={(e) => {
            if (isCardClickable && (e.key === 'Enter' || e.key === ' ')) {
              e.stopPropagation();
              this.redirectTo(cardData);
            }
          }}
          tabIndex={isCardClickable ? 0 : -1}
          aria-label={isCardClickable ? "Click to navigate to content" : "Decorative video"}
          role={isCardClickable ? "button" : "presentation"}
        >
          <source src={videoUrl} type={mime} />
        </video>
      );
    } else if (showImage) {
      mediaElement = (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <img
          src={imageUrl}
          alt={isCardClickable ? "Click to navigate to content" : "Decorative image"}
          className={isCardClickable ? "cursor-pointer" : undefined}
          onClick={(e) => {
            if (isCardClickable) {
              e.stopPropagation();
              this.redirectTo(cardData);
            }
          }}
          onKeyDown={(e) => {
            if (isCardClickable && e.key === 'Enter') {
              e.stopPropagation();
              this.redirectTo(cardData);
            }
          }}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={isCardClickable ? 0 : -1}
          role={isCardClickable ? "button" : "presentation"}
        />
      );
    } else {
      mediaElement = <div className={styles.videoPlaceholder} />;
    }

    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className={[styles.videoContainer, fadeOutClass, cursorClass].join(' ')}
        onClick={(e) => {
          if (isCardClickable) {
            e.stopPropagation();
            this.redirectTo(cardData);
          }
        }}
        onKeyDown={(e) => isCardClickable && e.key === 'Enter' && this.handleClick(cardData)}
        aria-label={isCardClickable ? 'Click to navigate to content' : undefined}
        role={isCardClickable ? "button" : "presentation"}
      >
        {mediaElement}
        {hasTextOverlay && (
          <div className={styles.imgWrapper}>
            <div className={styles.innerWrapper}>
              <div className={styles.inner}>
                <div className={styles.leftChildContainer}>
                  {preheaderText && <div className={styles.preheaderText}>{preheaderText}</div>}
                  {title && (
                  <h2 className={`${styles.title} font-aryaDouble`} style={{ color: titleColor, fontStyle: titleItalic === '1' || titleItalic === 1 ? 'italic' : 'normal' }}>
                    {titleBold && titleBold !== '' && (
                    <>
                      <span style={{ fontWeight: 'bold' }}>{titleBold}</span>
                      <br />
                    </>
)}
                    {title}
                  </h2>
)}
                  {description && <p className={styles.description}>{description}</p>}
                  {actionButton}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

HeroIntroPromo.propTypes = {
  cardData: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    extras: PropTypes.shape({
      template: PropTypes.string,
      desktopVideoUrl: PropTypes.string,
      mobileVideoUrl: PropTypes.string,
      desktopImageUrl: PropTypes.string,
      mobileImageUrl: PropTypes.string,
      mime: PropTypes.string,
      preheaderText: PropTypes.string,
      actionButtonText: PropTypes.string,
      actionButtonTextColor: PropTypes.string,
      titleColor: PropTypes.string,
      titleBold: PropTypes.string,
      titleItalic: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  }).isRequired,
  contentCardRefreshDelay: PropTypes.number.isRequired,
  setCarouselSpeed: PropTypes.func.isRequired
};

export const mapStateToProps = state => {
  const { contentCardRefreshDelay } = state.braze;
  return {
    contentCardRefreshDelay
  };
};

export const mapDispatchToProps = (dispatch) => ({
  setCarouselSpeed : (value) => dispatch(elementsActions.setCarouselSpeed(value)),
  contentCardSession: (value) => dispatch(brazeActions.contentCardSession(value)),
  setProductCategory: (value) => dispatch(elementsActions.setProductCategory(value)),
  setHeroContentCardData: (value) => dispatch(elementsActions.setHeroContentCardData(value))
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HeroIntroPromo));